import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';

const UNRESTRICTED_ROUTES = new Set();

function hasPermission(permissions, requiredPermission) {
  if (!requiredPermission) return true;
  return Array.isArray(permissions?.permissions) && permissions.permissions.includes(requiredPermission);
}

/**
 * Backend sends "/webview" once; React paths may be /webview, /webview/:laneSlug,
 * /webview-intrusion, or /webview-combined.
 */
function pathMatchesAllowedRoutes(pathname, allowedRoutes) {
  if (!Array.isArray(allowedRoutes)) return false;
  if (allowedRoutes.includes(pathname)) return true;
  if (pathname === '/webview' || pathname.startsWith('/webview/')) {
    return allowedRoutes.includes('/webview');
  }
  if (pathname === '/webview-intrusion' || pathname.startsWith('/webview-intrusion/')) {
    return allowedRoutes.includes('/webview-intrusion');
  }
  if (pathname === '/webview-combined' || pathname.startsWith('/webview-combined/')) {
    return allowedRoutes.includes('/webview') && allowedRoutes.includes('/webview-intrusion');
  }
  // Handle dynamic employee detail routes
  if (pathname.startsWith('/employee/') && allowedRoutes.includes('/employees')) {
    return true;
  }
  // Handle dynamic vendor detail routes
  if (pathname.startsWith('/vendor/') && allowedRoutes.includes('/vendors')) {
    return true;
  }
  // Handle dynamic visitor detail routes
  if (pathname.startsWith('/visitor/') && allowedRoutes.includes('/visitors')) {
    return true;
  }
  return false;
}

/**
 * ProtectedRoute component for role-based routing
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Component to render if access is granted
 * @param {string[]} props.allowedRoles - Array of allowed roles ('admin', 'intrusion', or both)
 * @param {boolean} props.requireAuth - Whether authentication is required (default: true)
 * @param {string} props.redirectTo - Custom redirect path (default: based on user type)
 */
const ProtectedRoute = ({ 
  children, 
  allowedRoles = [], 
  requireAuth = true,
  redirectTo = null,
  requiredPermission = null,
  requireSuperAdmin = false,
}) => {
  const { isAuthenticated, userType, loading, permissions, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Validate route is in allowed_routes (additional security layer)
  useEffect(() => {
    if (permissions && !loading && isAuthenticated && userType) {
      const currentPath = location.pathname;
      if (UNRESTRICTED_ROUTES.has(currentPath)) {
        return;
      }

      // Check if route is in allowed_routes from backend
      if (permissions.allowed_routes && !pathMatchesAllowedRoutes(currentPath, permissions.allowed_routes)) {
        console.warn(`Route ${currentPath} not in allowed_routes, redirecting...`);
        const redirectPath = permissions.dashboard_path || redirectTo || '/dashboard';
        navigate(redirectPath, { replace: true });
      }
    }
  }, [location.pathname, permissions, loading, isAuthenticated, userType, navigate, redirectTo]);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireSuperAdmin && !isSuperAdmin) {
    const fallbackPath = permissions?.dashboard_path || redirectTo || '/dashboard';
    return <Navigate to={fallbackPath} replace />;
  }

  if (requiredPermission && !hasPermission(permissions, requiredPermission)) {
    const fallbackPath = permissions?.dashboard_path || redirectTo || '/dashboard';
    return <Navigate to={fallbackPath} replace />;
  }

  // Enforce backend route permissions before rendering protected content
  if (
    permissions &&
    Array.isArray(permissions.allowed_routes) &&
    !UNRESTRICTED_ROUTES.has(location.pathname) &&
    !pathMatchesAllowedRoutes(location.pathname, permissions.allowed_routes)
  ) {
    const fallbackPath = permissions.dashboard_path || redirectTo || '/dashboard';
    return <Navigate to={fallbackPath} replace />;
  }

  // If no roles specified, allow any authenticated user who passed backend route permissions
  if (allowedRoles.length === 0) {
    return children;
  }

  // Check if user has required role
  if (userType && allowedRoles.includes(userType)) {
    return children;
  }

  // Redirect based on user type or custom redirect path
  let redirectPath = redirectTo;
  
  if (!redirectPath) {
    if (permissions?.dashboard_path) {
      redirectPath = permissions.dashboard_path;
    } else if (userType === 'admin') {
      redirectPath = '/dashboard';
    } else if (userType === 'intrusion') {
      redirectPath = '/intrusion-dashboard';
    } else {
      redirectPath = '/login';
    }
  }

  // Avoid redirecting to the exact same route (can cause a blank loop on refresh)
  if (redirectPath === location.pathname) {
    redirectPath = '/login';
  }

  return <Navigate to={redirectPath} replace />;
};

export default ProtectedRoute;
